import * as React from 'react';

import { useQuery } from '@apollo/client';
import type {
  ProductCard_Course,
  ProductCard_ProductCard,
  ProductCard_Specialization,
} from '__generated__/graphql-types-do-not-use';

import { useParams } from 'js/lib/useRouter';

import { useTracker } from '@coursera/event-pulse/react';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import collectionAttributesQuery from 'bundles/enterprise-admin-collection-discovery/queries/CollectionAttributes.graphql';
import collectionsWithAttributesQuery from 'bundles/enterprise-admin-collection-discovery/queries/CollectionsWithAttributes.graphql';
import GetCollectionWithAttributes from 'bundles/enterprise-admin-collection-discovery/queries/GetCollectionWithAttributes.graphql';
import type {
  CollectionDiscoveryAttributesQuery,
  CollectionDiscoveryAttributesQueryVariables,
} from 'bundles/enterprise-admin-collection-discovery/queries/__generated__/CollectionAttributes';
import type {
  CollectionWithAttributesQuery,
  CollectionWithAttributesQueryVariables,
} from 'bundles/enterprise-admin-collection-discovery/queries/__generated__/CollectionsWithAttributes';
import type {
  GetCollectionWithAttributesQuery,
  GetCollectionWithAttributesQueryVariables,
} from 'bundles/enterprise-admin-collection-discovery/queries/__generated__/GetCollectionWithAttributes';
import type { Collection, Tag } from 'bundles/enterprise-admin-collection-discovery/types';
import { useProgramBySlug } from 'bundles/enterprise-admin-hooks';
import { useNaptime } from 'bundles/naptimejs';
import ExternallyAccessibleNostosV1 from 'bundles/naptimejs/resources/externallyAccessibleNostos.v1';
import type { Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';

export const useCollections = (categoryId: string, tagId?: string, programId?: string) => {
  const org = useThirdPartyOrganization();
  const input = (
    {
      skills: {
        skill: {
          skillId: tagId as string,
        },
      },
      domains: {
        domain: {
          domainId: tagId as string,
        },
      },
      'academic-disciplines': {
        academicDiscipline: {
          value: tagId as string,
        },
      },
      'custom-categories':
        org.organizationType === 'CAMPUS'
          ? { educationalGoal: { value: tagId as string } }
          : {
              organizationGoal: {
                value: tagId as string,
              },
            },
    } as const
  )[categoryId];
  const groupBy = (
    {
      skills: 'SKILL',
      domains: 'DOMAIN',
      'academic-disciplines': 'ACADEMIC_DISCIPLINE',
      'custom-categories': org.organizationType === 'CAMPUS' ? 'EDUCATIONAL_GOAL' : 'ORGANIZATION_GOAL',
    } as const
  )[categoryId];

  const { data, loading } = useQuery<CollectionWithAttributesQuery, CollectionWithAttributesQueryVariables>(
    collectionsWithAttributesQuery,
    {
      variables: {
        visibilityScopes: ['ENTERPRISE_ADMIN'],
        additionalInput: {
          // eslint-disable-next-line camelcase
          enterprise_program_id: programId,
        },
        limit: 100,
        attributeInputs: input ? [input] : [],
        groupAttributesBy: groupBy
          ? [
              {
                attributeType: groupBy,
                limit: 100,
              },
            ]
          : [],
      },
      skip: !tagId || !input || !groupBy || !programId,
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );
  if (!input || !groupBy || !programId) return {};
  const collections = data?.CollectionWithAttributes?.queryCollectionsByAttributes.elements;
  return {
    loading,
    collections: collections
      ?.filter((_) => _ !== null && _ !== undefined)
      ?.map(
        (_) =>
          ({
            id: _.collectionAttributes.collectionId,
            title: _.description,
            items: _.entities?.elements ?? [],
          } as Collection)
      )
      ?.filter((_) => Boolean(_.title))
      .sort((a, b) => a.title.localeCompare(b.title)),
  };
};

export const useAddDynamicCollectionsToProgram = () => {
  const { thirdPartySlug, programSlug } = useParams();
  const track = useTracker();
  const program = useProgramBySlug(programSlug);
  const curriculumId = program?.metadata.curriculumId;
  const {
    data: { curriculum: originalCurriculum },
    naptime,
  } = useNaptime<{ curriculum: ProgramCurriculumsV1 }>(
    () => ({
      curriculum: curriculumId
        ? ProgramCurriculumsV1.get(curriculumId, {
            fields: ['definition', 'typeName'],
            params: {
              includeClips: true,
            },
          })
        : undefined,
    }),
    [curriculumId]
  );

  const addDynamicCollectionsToProgram = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    async (collections: Track[]) => {
      const newCurriculum = structuredClone(originalCurriculum);
      if (newCurriculum.typeName === 'wildcardCurriculum') {
        newCurriculum.definition.electiveTracks.push(...collections);
      } else if (newCurriculum.typeName === 'tracksCurriculum') {
        newCurriculum.definition.coreTracks.push(...collections);
      }

      await naptime.executeMutation(
        ProgramCurriculumsV1.update(
          curriculumId + '?allowClearingClips=true&updateSource=CURATED_COLLECTION&skipUnauthorizedProducts=true',
          newCurriculum
        )
      );
      const curatedCollectionIds = collections
        .map((_) => _.referenceCollectionConfiguration?.referenceCollectionId.definition.id)
        .filter((_) => _ !== undefined);
      track('add_curated_enterprise_collections', {
        curatedCollectionIds,
        enterprise: {
          organizationSlug: thirdPartySlug,
          programSlug,
        },
      });

      await naptime.refreshData({ resources: [ProgramCurriculumsV1.RESOURCE_NAME] });
    },
    [curriculumId, naptime, originalCurriculum, track, thirdPartySlug, programSlug]
  );

  const checkIfReferenceCollectionIsInProgram = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (id: string) => {
      return [
        ...(originalCurriculum?.definition?.electiveTracks ?? []),
        ...(originalCurriculum?.definition?.coreTracks ?? []),
      ].some((_) => _?.referenceCollectionConfiguration?.referenceCollectionId?.definition?.id === id);
    },
    [originalCurriculum?.definition?.coreTracks, originalCurriculum?.definition?.electiveTracks]
  );
  return {
    addDynamicCollectionsToProgram,
    checkIfReferenceCollectionIsInProgram,
    loadingCurriculum: !program || !originalCurriculum,
    program,
  };
};

export const useCollectionAttributes = () => {
  const org = useThirdPartyOrganization();
  const { data } = useQuery<CollectionDiscoveryAttributesQuery, CollectionDiscoveryAttributesQueryVariables>(
    collectionAttributesQuery,
    {
      variables: {
        visibilityScopes: ['ENTERPRISE_ADMIN'],
        groupAttributesBy: [
          {
            attributeType: 'DOMAIN',
            limit: 100,
          },
          {
            attributeType: 'SKILL',
            limit: 1000,
          },
          {
            attributeType: 'ACADEMIC_DISCIPLINE',
            limit: 100,
          },
          {
            attributeType: org.organizationType === 'CAMPUS' ? 'EDUCATIONAL_GOAL' : 'ORGANIZATION_GOAL',
            limit: 100,
          },
        ],
      },
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
    }
  );
  const attributes = data?.CollectionAttributes?.getAllDistinctCollectionAttributes;
  const skills = React.useMemo(() => {
    return (attributes?.find((_: $TSFixMe) => _.attributeType === 'SKILL')?.attributes?.elements ?? []).map(
      (_: $TSFixMe) => ({ id: _.id, name: _.skillName, domains: _.domains })
    ) as Tag[];
  }, [attributes]);
  const academicDisciplines = (
    attributes?.find((_: $TSFixMe) => _.attributeType === 'ACADEMIC_DISCIPLINE')?.attributes?.elements ?? []
  ).map((_: $TSFixMe) => ({ id: _.value, name: _.value, domains: [] })) as Tag[];
  const goal = org.organizationType === 'CAMPUS' ? 'EDUCATIONAL_GOAL' : 'ORGANIZATION_GOAL';
  const organizationalGoals = (
    attributes?.find((_: $TSFixMe) => _.attributeType === goal)?.attributes?.elements ?? []
  ).map((_: $TSFixMe) => ({ id: _.value, name: _.value, domains: [] })) as Tag[];
  return { skills, academicDisciplines, organizationalGoals, attributes };
};

type Partner = {
  name: string;
  logoUrl: string;
};

type ProductWithPartner = {
  id: string;
  name: string;
  slug: string;
  logo: string;
  productType: 'COURSE' | 'SPECIALIZATION';
  partners: Partner[];
};

export const useCollectionWithAttributes = (collectionId: string, programId?: string) => {
  const { data, error, loading } = useQuery<
    GetCollectionWithAttributesQuery,
    GetCollectionWithAttributesQueryVariables
  >(GetCollectionWithAttributes, {
    variables: {
      collectionId,
      collectionType: 'PRESET',
      additionalInput: {
        // eslint-disable-next-line camelcase
        enterprise_program_id: programId,
      },
    },
    skip: !collectionId || !programId,
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
  });

  const collectionTitle = data?.CollectionWithAttributes?.getCollectionWithAttributes?.description;
  const collectionProducts = data?.CollectionWithAttributes?.getCollectionWithAttributes?.entities?.elements
    ?.filter((_) => _ !== null && _ !== undefined)
    ?.map((collectionEntity) => {
      const productCard = collectionEntity as ProductCard_ProductCard;
      const isCourse = productCard.canonicalType === 'COURSE';
      const isSpecialization = productCard.canonicalType === 'SPECIALIZATION';

      let product: ProductCard_Course | ProductCard_Specialization;
      let logo: string | null;
      if (isCourse) {
        product = productCard.productTypeAttributes as ProductCard_Course;
        logo = product.canonical.promoPhotoUrl || null;
      } else if (isSpecialization) {
        product = productCard.productTypeAttributes as ProductCard_Specialization;
        logo = product.canonical.cardImageUrl || null;
      } else {
        throw new Error(`Unknown product type for entity within collectionId: ${collectionId}`);
      }
      const partners = product.canonical.partners.map((partner) => {
        return {
          name: partner.name,
          logoUrl: partner.squareLogo,
        };
      });
      return {
        id: product.canonical.id,
        name: product.canonical.name,
        slug: product.canonical.slug,
        productType: productCard.canonicalType,
        logo,
        partners,
      } as ProductWithPartner;
    });

  return {
    collectionTitle,
    collectionProducts,
    error,
    loading,
  };
};

export const useTopSkills = () => {
  const { data: naptimeData } = useNaptime<{ skillStats?: { content?: { attributeValue?: string[] } }[] }>(
    () => ({
      skillStats: ExternallyAccessibleNostosV1.finder('getAllProperties', {
        params: { job_name: 'curated_collections_ordering', keys: 'SKILL' },
      }),
    }),
    []
  );
  const topSkills = React.useMemo(
    () => (naptimeData?.skillStats?.[0]?.content?.attributeValue as string[]) ?? [],
    [naptimeData]
  );
  return { topSkills };
};

export const useTopGoals = () => {
  const org = useThirdPartyOrganization();
  const { data: naptimeData } = useNaptime<{ goalStats?: { content?: { attributeValue?: string[] } }[] }>(
    () => ({
      goalStats: ExternallyAccessibleNostosV1.finder('getAllProperties', {
        params: {
          job_name: 'curated_collections_ordering',
          keys: org.organizationType === 'CAMPUS' ? 'EDUCATIONAL_GOAL' : 'ORGANIZATION_GOAL',
        },
      }),
    }),
    []
  );
  const topGoals = React.useMemo(
    () => (naptimeData?.goalStats?.[0]?.content?.attributeValue as string[]) ?? [],
    [naptimeData]
  );
  return { topGoals };
};
